import { Output, Mp4OutputFormat, WebMOutputFormat, BufferTarget, CanvasSource, Quality, getFirstEncodableVideoCodec } from "mediabunny";
import { VIDEO_W, VIDEO_H, prepareTimeline, drawTimelineFrame } from "./cityVideoRenderer";
import { downloadBlob } from "./exportChartPng";

const FPS = 30, HOLD_S = 1.6, MOVE_S = 1.0, LAST_HOLD_S = 2.6;

export const videoExportSupported = () => typeof window !== "undefined" && typeof window.VideoEncoder === "function";

/** Pick container + codec the browser can actually encode: H.264 MP4 (LinkedIn-ready) → VP9/AV1 MP4 → WebM. */
async function pickFormat() {
  const size = { width: VIDEO_W, height: VIDEO_H, bitrate: 8e6 };
  const mp4 = await getFirstEncodableVideoCodec(["avc", "vp9", "av1"], size);
  if (mp4) return { codec: mp4, format: new Mp4OutputFormat({ fastStart: "in-memory" }), ext: "mp4", mime: "video/mp4" };
  const webm = await getFirstEncodableVideoCodec(["vp9", "vp8", "av1"], size);
  if (webm) return { codec: webm, format: new WebMOutputFormat(), ext: "webm", mime: "video/webm" };
  return null;
}

/** Schedule: hold on each year, then an eased move to the next; the last year holds longer. */
function schedule(n) {
  const steps = [];
  for (let i = 0; i < n; i++) {
    const hold = Math.round((i === n - 1 ? LAST_HOLD_S : HOLD_S) * FPS);
    for (let k = 0; k < hold; k++) steps.push([i, 0]);
    if (i < n - 1) { const move = Math.round(MOVE_S * FPS); for (let k = 1; k <= move; k++) steps.push([i, k / move]); }
  }
  return steps;
}

/**
 * Timeline → video, rendered frame by frame on an offscreen 1920×1080 canvas (smooth eased transitions, 30 fps)
 * and encoded with WebCodecs through Mediabunny. Resolves with { ext, seconds, codec }.
 */
export async function exportTimelineVideo({ frames, strings, highlightIt, marked, fileBase, onProgress }) {
  if (!videoExportSupported()) throw new Error("webcodecs-unavailable");
  const choice = await pickFormat();
  if (!choice) throw new Error("no-encoder");
  const canvas = document.createElement("canvas");
  canvas.width = VIDEO_W; canvas.height = VIDEO_H;
  const ctx = canvas.getContext("2d", { alpha: false });
  const serifFamily = getComputedStyle(document.querySelector(".pulse-serif") || document.body).fontFamily || "Georgia, serif";
  const sansFamily = getComputedStyle(document.body).fontFamily || "system-ui, sans-serif";
  const tl = prepareTimeline(frames, { sansFamily, measureCtx: ctx });

  const output = new Output({ format: choice.format, target: new BufferTarget() });
  const source = new CanvasSource(canvas, { codec: choice.codec, quality: new Quality({ bitrate: 8e6 }), keyFrameInterval: 2 });
  output.addVideoTrack(source, { frameRate: FPS });
  await output.start();
  const steps = schedule(frames.length);
  try {
    for (let k = 0; k < steps.length; k++) {
      const [i, t] = steps[k];
      drawTimelineFrame(ctx, tl, i, t, { strings, highlightIt, marked, serifFamily, sansFamily });
      await source.add(k / FPS, 1 / FPS);
      if (onProgress && k % 6 === 0) onProgress(k / steps.length);
    }
    source.close();
    await output.finalize();
  } catch (e) {
    try { await output.cancel(); } catch { /* already closed */ }
    throw e;
  }
  downloadBlob(new Blob([output.target.buffer], { type: choice.mime }), `${fileBase}.${choice.ext}`);
  return { ext: choice.ext, seconds: Math.round(steps.length / FPS), codec: choice.codec };
}
